import { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDateTime as fmtDT } from '../utils/dateFormat';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Notebook, CheckSquare, EnvelopeSimple, FileText, Users, MagnifyingGlass, FunnelSimple, CaretDown, Check, ShieldCheck, FirstAidKit, Baby, Buildings, ChatCircleDots, SignIn } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';

const MODUL_CONFIG = {
  klijent:     { icon: Users, color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Klijent' },
  dnevnik:     { icon: Notebook, color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Dnevnik' },
  zadatak:     { icon: CheckSquare, color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Zadatak' },
  irms:        { icon: Buildings, color: 'bg-indigo-100 text-indigo-700 border-indigo-200', label: 'IRMS' },
  bolovanje:   { icon: FirstAidKit, color: 'bg-red-100 text-red-700 border-red-200', label: 'Bolovanje' },
  porodiljsko: { icon: Baby, color: 'bg-pink-100 text-pink-700 border-pink-200', label: 'Porodiljsko' },
  cestitke:    { icon: EnvelopeSimple, color: 'bg-green-100 text-green-700 border-green-200', label: 'Cestitke' },
  pdv:         { icon: FileText, color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'PDV' },
  zahtjev:     { icon: FileText, color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Zahtjev' },
  korisnik:    { icon: ShieldCheck, color: 'bg-teal-100 text-teal-700 border-teal-200', label: 'Korisnik' },
  auth:        { icon: SignIn, color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Prijava' },
};

const AKCIJA_COLORS = {
  kreiranje:      'bg-green-50 text-green-700 border-green-200',
  izmjena:        'bg-amber-50 text-amber-700 border-amber-200',
  brisanje:       'bg-red-50 text-red-700 border-red-200',
  slanje_email:   'bg-blue-50 text-blue-700 border-blue-200',
  slanje_viber:   'bg-purple-50 text-purple-700 border-purple-200',
  status_promjena:'bg-sky-50 text-sky-700 border-sky-200',
  login:          'bg-gray-50 text-gray-600 border-gray-200',
};

function SearchableFilter({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);
  const selected = options.find(o => o.value === value);
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex items-center justify-between w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-9 hover:border-slate-400">
        <span className={selected && selected.value !== 'all' ? 'text-slate-900 truncate' : 'text-slate-500 truncate'}>{selected ? selected.label : placeholder}</span>
        <CaretDown size={14} className="text-slate-400 ml-2" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-52 overflow-auto">
          <div className="p-1.5 border-b"><input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretrazi..." className="w-full px-2 py-1 text-sm border border-slate-200 rounded" /></div>
          {filtered.map(o => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 flex items-center gap-2 ${o.value === value ? 'bg-blue-50 text-blue-700' : ''}`}>
              {o.value === value && <Check size={14} />}
              <span className={o.value === value ? 'font-medium' : ''}>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HronologijaPage() {
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterModul, setFilterModul] = useState('all');
  const [filterKorisnik, setFilterKorisnik] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDatumOd, setFilterDatumOd] = useState('');
  const [filterDatumDo, setFilterDatumDo] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const params = {};
      if (filterModul !== 'all') params.tip = filterModul;
      if (filterKorisnik !== 'all') params.korisnik_id = filterKorisnik;
      if (filterClient !== 'all') params.client_id = filterClient;
      if (filterDatumOd) params.datum_od = filterDatumOd;
      if (filterDatumDo) params.datum_do = filterDatumDo;

      const [ev, cl, us] = await Promise.all([
        api.get('/hronologija', { params }),
        api.get('/clients'),
        api.get('/users'),
      ]);
      setEvents(ev.data);
      setClients(cl.data);
      setUsers(us.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Samo administrator moze pristupiti hronologiji');
      } else {
        toast.error('Greska pri ucitavanju');
      }
    }
    finally { setLoading(false); }
  };

  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => (a.skraceni_naziv || a.naziv_firme || '').localeCompare(b.skraceni_naziv || b.naziv_firme || '')),
    [clients]
  );

  const getClientName = (id) => {
    const c = clients.find(c => c.id === id);
    return c ? (c.skraceni_naziv || c.naziv_firme) : '';
  };

  const filtered = useMemo(() => {
    return events.filter(e => {
      if (filterSearch) {
        const s = filterSearch.toLowerCase();
        const clientName = getClientName(e.client_id).toLowerCase();
        if (!e.opis?.toLowerCase().includes(s) && !e.korisnik_ime?.toLowerCase().includes(s) && !clientName.includes(s) && !e.modul?.toLowerCase().includes(s) && !e.akcija?.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [events, filterSearch, clients]);

  const formatDT = (dt) => fmtDT(dt);

  // Group by date
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(e => {
      const dt = e.datum ? new Date(e.datum) : null;
      const key = dt && !isNaN(dt.getTime()) ? `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}` : 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const formatDateHeader = (key) => {
    if (key === 'unknown') return 'Nepoznat datum';
    const [y, m, d] = key.split('-');
    return `${d}.${m}.${y}`;
  };

  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return (
      <div className="p-12 text-center" data-testid="hronologija-page">
        <ShieldCheck size={48} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500">Samo administrator moze pristupiti hronologiji</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hronologija-page">
      <div>
        <h3 className="text-lg font-medium text-slate-900">Hronologija</h3>
        <p className="text-sm text-slate-600 mt-1">Sve promjene u sistemu - vidljivo samo administratoru</p>
      </div>

      {/* Filters */}
      <Card className="p-4" data-testid="hronologija-filters">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Pretraga..." className="pl-9 bg-white border-slate-300 text-sm h-9" />
          </div>
          <SearchableFilter
            options={[{ value: 'all', label: 'Svi moduli' }, ...Object.entries(MODUL_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
            value={filterModul} onChange={v => { setFilterModul(v); setTimeout(fetchAll, 100); }} placeholder="Modul" />
          <SearchableFilter
            options={[{ value: 'all', label: 'Svi korisnici' }, ...users.map(u => ({ value: u.id, label: u.name }))]}
            value={filterKorisnik} onChange={v => { setFilterKorisnik(v); setTimeout(fetchAll, 100); }} placeholder="Korisnik" />
          <SearchableFilter
            options={[{ value: 'all', label: 'Svi klijenti' }, ...sortedClients.map(c => ({ value: c.id, label: c.skraceni_naziv || c.naziv_firme }))]}
            value={filterClient} onChange={v => { setFilterClient(v); setTimeout(fetchAll, 100); }} placeholder="Klijent" />
          <DateInput value={filterDatumOd} onChange={e => { setFilterDatumOd(e.target.value); setTimeout(fetchAll, 100); }} className="border-slate-300 text-sm" />
          <DateInput value={filterDatumDo} onChange={e => { setFilterDatumDo(e.target.value); setTimeout(fetchAll, 100); }} className="border-slate-300 text-sm" />
          <div className="flex items-center text-xs text-slate-500 font-medium">
            <FunnelSimple size={14} className="mr-1" /> {filtered.length} zapisa
          </div>
        </div>
      </Card>

      {/* Timeline */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><p className="text-slate-500">Ucitavanje...</p></div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center"><p className="text-slate-500">Nema aktivnosti za prikaz</p></Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(([dateKey, dayEvents]) => (
            <div key={dateKey}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{formatDateHeader(dateKey)}</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="space-y-1.5">
                {dayEvents.map((e, idx) => {
                  const cfg = MODUL_CONFIG[e.modul] || MODUL_CONFIG.klijent;
                  const Icon = cfg.icon;
                  const clientName = getClientName(e.client_id);
                  const akcijaColor = AKCIJA_COLORS[e.akcija] || 'bg-slate-50 text-slate-600 border-slate-200';
                  return (
                    <div key={`${dateKey}-${idx}`} className="flex items-start gap-3 px-4 py-2.5 bg-white rounded-lg border border-slate-100 hover:border-slate-200 transition-colors">
                      <div className={`mt-0.5 p-1.5 rounded ${cfg.color}`}>
                        <Icon size={16} weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${cfg.color}`}>{cfg.label}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${akcijaColor}`}>{e.akcija}</span>
                        </div>
                        <p className="text-sm text-slate-800 mt-1" title={e.opis}>{e.opis || '-'}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          {e.korisnik_ime && <span className="font-medium text-slate-500">{e.korisnik_ime}</span>}
                          {clientName && <span>{clientName}</span>}
                          {e.detalji && <span className="italic">{e.detalji}</span>}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 whitespace-nowrap mt-1">{formatDT(e.datum)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
